import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { KeycloakService } from 'keycloak-angular';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Container } from '../../containers/container';
import { Group } from '../../shared/entities/group/group';
import { Location } from '../../shared/entities/location/location';
import { Role } from '../../shared/entities/role/role';
import { User } from '../../shared/entities/user/user';
import { GrowthBookService } from '../../shared/services/growthbook.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LocationService } from '../../shared/services/location/location.service';
import { RolesService } from '../../shared/services/roles/roles.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { UsersService } from '../../shared/services/users/users.service';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { EditDialogService } from '../editDialog/editDialogService';
import { UserPreferredLanguageDialogComponent } from '../user-preferred-language-dialog/user-preferred-language-dialog.component';
import * as fromGraphQl from './graphql';


interface Setting {
  key: string;
  value: any;
}

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent
  extends Container<fromGraphQl.UserQueryResponse>
  implements OnInit, OnDestroy {

  dialogRef: DynamicDialogRef | undefined;

  public familyName: string;
  public givenName: string;

  public user: User;
  public id: string;
  public routeSubscription: Subscription;
  public UserClass = User;
  public locations: Location[];
  public availableRoles: Role[];
  public usersRoles: Role[];
  public usersRolesFiltered: Role[];

  public userGroups: Group[];
  public selectedRoles: Role[];

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    loadingService: LoadingService,
    apollo: Apollo,
    public dialogService: EditDialogService,
    private confirmDialogService: ConfirmDialogService,
    @Inject('env') environment : any,
    private userService: UsersService,
    private toastService: ToastService,
    private locationService: LocationService,
    private rolesService: RolesService,
    private keycloakAngular: KeycloakService,
    private growthBookService: GrowthBookService
  ) {
    super(apollo, loadingService, fromGraphQl.USERS_QUERY, environment);
  }

  ngOnInit(): void {
    this.familyName = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['family_name']
      : '';
    this.givenName = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['given_name']
      : '';
    this.id = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['id']
      : '';
    this.subscribeToQuery({
      id: this.id,
    });
    this.locationService.getLocations().pipe(take(1)).subscribe((locations) => {
      this.locations = locations;
    });
    this.rolesService.getRoles().pipe(take(1)).subscribe((roles) => {
      this.availableRoles = roles;
      this.filterUserRoles();
      this.filterUserGroups();
    });
  }

  getLabel(): string{
    return this.givenName + ' ' + this.familyName;
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  handleSubscriptionResult(data: fromGraphQl.UserQueryResponse): void {
    this.loadingService.setLoading(false);
    if (data) {
      this.queryRef.stopPolling();
      if (data.user) {
        this.user = new User(data.user);
        this.filterUserRoles();
      }
    }
  }

  getUserLocations(): string {
    // get the location ids of the user an map them to the location names
    // if it cannot find locations, skip them

    let assignedLocations = this.user?.locationIds?.map((locationId) => {
      const location = this.locations.find((location) => location.id === locationId);
      return location?.name;
    });
    // filter all undefined values from array
    return assignedLocations?.filter((location) => location !== undefined).join(', ');
  }

  filterUserRoles(): void {
    if (this.user && this.availableRoles) {
      this.usersRoles = this.user.roles.filter((role) => {
        return this.availableRoles.find((availableRole) => availableRole.id === role.id);
      });
      this.usersRolesFiltered = this.usersRoles;
    }
  }

  filterUserGroups(): void {
    // we want to filter out the tenant group
    let tenantId = this.keycloakAngular.getKeycloakInstance()
      ? (this.keycloakAngular.getKeycloakInstance()?.idTokenParsed as any)['tenant_id']
      : '';
    if (this.user && this.user.groups) {
      this.userGroups = this.user.groups.filter((group) => {
        return group.name !== tenantId;
      });
    }
  }

  loadData(event: TableLazyLoadEvent) {
    //filtering
    let normalizedSearchTerm = (event.globalFilter as string)?.toLowerCase();

    // pagination
    if (normalizedSearchTerm) {
      this.usersRolesFiltered = this.usersRoles.filter((role) =>
        role.label?.toLowerCase().includes(normalizedSearchTerm)
      );
    }
    else {
      this.usersRolesFiltered = this.usersRoles;
    }
  }

  editPreferredLanguage() {
    this.dialogRef = this.dialogService.open(UserPreferredLanguageDialogComponent, {
      header: 'core.user.preferredLanguageDialog.header',
      data: this.user
    });
    this.dialogRef.onClose.subscribe((result) => {
      if(result?.preferredLanguage) {
        this.userService.updatePreferredLanguage(result?.preferredLanguage, () => {
          this.toastService.addToastWithMessage('core.user.preferredLanguageDialog.successToastMessage');
        });
      }
    });
  }

  getPreferredLanguage(): string {
    let preferredLanguage = this.userService.getCurrentUsersPreferredlanguage();
    return preferredLanguage ? 'core.common.language.' + preferredLanguage : '-';
  }

  isMultiLanguageEnabled(): boolean {
    return this.growthBookService.isFeatureEnabled('multiLanguage');
  }
}
